// Solar inverter style 270° arc gauge — pro version.
// ▸ Gradient stroke (configurable), soft glow, smooth value tween
// ▸ Icon centred inside the arc, value + unit below
// ▸ "Off grid"-style top label, optional corner badges
// ▸ disabled = greyed out for unused features (e.g. battery on PV-only sites)

import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { useAnimatedNumber } from "./AnimatedNumber";

const ARC_RATIO = 0.75; // 270°

export default function Gauge({
  value = 0,
  max = 50,
  unit = "KW",
  color = "#22c55e",
  colorEnd,                       // optional second stop for the gradient
  trackColor = "#eef0f4",
  size = 200,
  strokeWidth = 12,
  icon,
  label,
  showMinMax = true,
  topLabel,
  topLabelColor,
  cornerBadges,
  disabled = false,
  active = true,                  // when true, give the icon a soft pulse
  gradientId,                     // unique id required when multiple gauges share a page
}) {
  const effectiveValue = disabled ? null : value;
  const fromColor = disabled ? "#c2c8d2" : color;
  const toColor   = disabled ? "#dde1e7" : (colorEnd || color);
  const effectiveTrack = disabled ? "#eef0f4" : trackColor;
  const effectiveBadges = disabled ? null : cornerBadges;

  const animValue = useAnimatedNumber(disabled ? 0 : (Number(value) || 0));

  const r = (size - strokeWidth) / 2 - 2;
  const cx = size / 2;
  const cy = size / 2;
  const c = 2 * Math.PI * r;
  const arcLen = c * ARC_RATIO;
  const pct = Math.max(0, Math.min(1, max ? Math.abs(animValue) / max : 0));
  const fill = disabled ? 0 : arcLen * pct;

  const gid = gradientId || `gg-${Math.abs(color.charCodeAt(1) || 0)}`;
  const glowId = `glow-${gid}`;

  return (
    <Stack alignItems="center" spacing={0.5} sx={{ position: "relative" }}>
      {topLabel && (
        <Typography
          variant="caption"
          sx={{
            color: topLabelColor || "error.main",
            fontWeight: 700,
            fontSize: "0.75rem",
            mb: 0.25,
            letterSpacing: 0.3,
          }}
        >
          {topLabel}
        </Typography>
      )}

      <Box sx={{ position: "relative", width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ overflow: "visible" }}
        >
          <defs>
            <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={fromColor} />
              <stop offset="100%" stopColor={toColor} />
            </linearGradient>
            <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* track */}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={effectiveTrack}
            strokeWidth={strokeWidth}
            strokeDasharray={`${arcLen} ${c}`}
            strokeLinecap="round"
            transform={`rotate(135 ${cx} ${cy})`}
          />
          {/* fill (gradient) with soft glow when active */}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={`url(#${gid})`}
            strokeWidth={strokeWidth}
            strokeDasharray={`${fill} ${c}`}
            strokeLinecap="round"
            transform={`rotate(135 ${cx} ${cy})`}
            filter={!disabled && pct > 0 ? `url(#${glowId})` : undefined}
            style={{ transition: "stroke-dasharray 0.18s linear" }}
          />
        </svg>

        {/* Centred icon */}
        {icon && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "30%",
              textAlign: "center",
              color: disabled ? "#c2c8d2" : "text.primary",
              opacity: disabled ? 0.55 : 1,
              "& svg": {
                fontSize: size * 0.32,
                animation: !disabled && active ? "icon-pulse 2.2s ease-in-out infinite" : "none",
              },
            }}
          >
            {icon}
          </Box>
        )}

        {/* Corner badges */}
        {effectiveBadges && (
          <>
            {effectiveBadges[0] && (
              <Typography variant="caption" sx={{ position: "absolute", top: "18%", left: "10%", fontWeight: 700, fontSize: "0.6875rem", color: "text.secondary" }}>
                {effectiveBadges[0]}
              </Typography>
            )}
            {effectiveBadges[1] && (
              <Typography variant="caption" sx={{ position: "absolute", top: "18%", right: "10%", fontWeight: 700, fontSize: "0.6875rem", color: "text.secondary" }}>
                {effectiveBadges[1]}
              </Typography>
            )}
          </>
        )}

        {/* Min/Max */}
        {showMinMax && (
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ position: "absolute", left: 8, right: 8, bottom: size * 0.16 }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: disabled ? "text.disabled" : "text.secondary" }}>0</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: disabled ? "text.disabled" : "text.secondary" }}>{max}</Typography>
          </Stack>
        )}

        {/* Value */}
        <Box sx={{ position: "absolute", left: 0, right: 0, bottom: size * 0.04, textAlign: "center" }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: size * 0.16,
              lineHeight: 1,
              color: fromColor,
              fontFamily: "monospace",
              letterSpacing: -0.5,
              textShadow: !disabled ? `0 0 12px ${fromColor}33` : "none",
            }}
          >
            {disabled ? "N/A" : (Number.isFinite(effectiveValue) ? Number(animValue).toFixed(2) : "—")}
          </Typography>
          <Typography variant="caption" sx={{ color: disabled ? "text.disabled" : "text.secondary", fontWeight: 600, fontSize: "0.6875rem", letterSpacing: 0.5 }}>
            {disabled ? "not available" : unit}
          </Typography>
        </Box>
      </Box>

      {label && (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: disabled ? "text.disabled" : "text.primary",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            fontSize: "0.6875rem",
            mt: 0.5,
          }}
        >
          {label}
        </Typography>
      )}
    </Stack>
  );
}
